/* eslint-disable max-lines */
import React, {
    useEffect, useMemo, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    opcodeAssistantService
} from 'shared/api';
import {
    bexHttpClient
} from 'shared/api/clients/bex.httpClient';
import {
    Activity as ActivityIcon, Download
} from 'lucide-react';

/**
 * UsagePage — DE Studio → Usage.
 *
 * Month-filterable table of per-assistant call counts, read from BeX server's
 * GET /cm/assistant-usage (which aggregates Project-O's per-org
 * `assistant_usage:*` Redis counters). The underlying counters carry a 90-day
 * TTL, so only the trailing ~3 months are queryable — the month picker is
 * scoped to that window.
 */

// Last `n` calendar months (current first), as { value: 'YYYY-MM', label }.
function recentMonths(n) {
    const out = [];
    const d = new Date();
    d.setDate(1);
    for (let i = 0; i < n; i++) {
        const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        out.push({ value: ym, label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }) });
        d.setMonth(d.getMonth() - 1);
    }
    return out;
}

const COLUMNS = [
    { key: 'total_calls', label: 'Total calls', align: 'right' },
    { key: 'success', label: 'Success', align: 'right' },
    { key: 'failure', label: 'Failure', align: 'right' },
    { key: 'typed', label: 'Typed', align: 'right' },
    { key: 'stream', label: 'Stream', align: 'right' },
    { key: 'active_days', label: 'Active days', align: 'right' },
];

export default function UsagePage() {
    const { oragID, subOragID } = useParams();
    const months = useMemo(() => recentMonths(3), []);
    const [month, setMonth] = useState(months[0].value);
    const [rows, setRows] = useState([]);
    const [namesById, setNamesById] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Assistant id → human title, so the table reads with names, not ids.
    useEffect(() => {
        if (!oragID || !subOragID) return;
        opcodeAssistantService.listOpcodeAssistants(oragID, subOragID)
            .then((res) => {
                const map = {};
                (Array.isArray(res?.data) ? res.data : []).forEach((a) => {
                    map[a.assistant_id] = a.title || a.assistant_id;
                });
                setNamesById(map);
            })
            .catch(() => { /* names are best-effort; fall back to ids */ });
    }, [oragID, subOragID]);

    useEffect(() => {
        if (!oragID || !subOragID || !month) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        bexHttpClient.get('/assistant-usage', {
            params: { org_id: oragID, sub_org_id: subOragID, month },
        })
            .then((res) => {
                if (cancelled) return;
                setRows(res?.data?.data?.assistants || []);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.response?.data?.error || err?.message || 'Failed to load usage');
                setRows([]);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [oragID, subOragID, month]);

    const totals = useMemo(() => {
        const t = { total_calls: 0, success: 0, failure: 0, typed: 0, stream: 0, active_days: 0 };
        rows.forEach((r) => COLUMNS.forEach((c) => { t[c.key] += Number(r[c.key]) || 0; }));
        return t;
    }, [rows]);

    // Export the current month's table as a CSV the user can open in Excel /
    // Sheets. Generated entirely client-side from the rows already loaded.
    function downloadCsv() {
        const esc = (v) => {
            const s = String(v ?? '');
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const header = ['Assistant', 'Assistant ID', ...COLUMNS.map((c) => c.label)];
        const body = rows.map((r) => [
            namesById[r.assistant_id] || r.assistant_id,
            r.assistant_id,
            ...COLUMNS.map((c) => Number(r[c.key]) || 0),
        ]);
        const totalRow = ['Total', '', ...COLUMNS.map((c) => totals[c.key])];
        const lines = [header, ...body, totalRow].map((row) => row.map(esc).join(','));
        const csv = lines.join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `assistant-usage_${oragID}_${month}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }

    return (
        <div className="ds-hub-page" style={{ padding: '20px 28px', boxSizing: 'border-box' }}>
            {/* header + month filter */}
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
                <div>
                    <h1 style={{ fontSize: 'var(--ds-text-2xl)', fontWeight: 700, margin: 0, color: 'var(--ds-text)' }}>Usage</h1>
                    <p style={{ margin: '4px 0 0', color: 'var(--ds-text-muted)', fontSize: 'var(--ds-text-sm)' }}>
                        Assistant call activity by month. Counts come from the live metering store
                        (kept ~90 days, so only recent months are available).
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontSize: 'var(--ds-text-xs)', color: 'var(--ds-text-subtle)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>Month</span>
                        <select
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            style={{
                                padding: '8px 12px', borderRadius: 8, fontSize: 'var(--ds-text-sm)',
                                border: '1px solid var(--ds-border-strong)', background: 'var(--ds-surface)',
                                color: 'var(--ds-text)', minWidth: 180,
                            }}
                        >
                            {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </label>
                    <button
                        type="button"
                        onClick={downloadCsv}
                        disabled={loading || rows.length === 0}
                        title={rows.length === 0 ? 'No data to export for this month' : 'Download this month as CSV'}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px',
                            borderRadius: 8, fontSize: 'var(--ds-text-sm)', fontWeight: 600,
                            border: '1px solid var(--ds-border-strong)', background: 'var(--ds-surface)',
                            color: 'var(--ds-text)', cursor: rows.length === 0 ? 'not-allowed' : 'pointer',
                            opacity: (loading || rows.length === 0) ? 0.55 : 1, height: 38,
                        }}
                    >
                        <Download size={15} /> Download CSV
                    </button>
                </div>
            </div>

            {error && (
                <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--ds-primary-soft)', color: 'var(--ds-primary-active)', fontSize: 'var(--ds-text-sm)', marginBottom: 12 }}>
                    {error}
                </div>
            )}

            <div style={{ border: '1px solid var(--ds-border)', borderRadius: 12, overflow: 'hidden', background: 'var(--ds-surface)' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 'var(--ds-text-sm)' }}>
                    <thead>
                        <tr>
                            <th style={thStyle('left')}>Assistant</th>
                            {COLUMNS.map((c) => <th key={c.key} style={thStyle(c.align)}>{c.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={COLUMNS.length + 1} style={tdCenter}>Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={COLUMNS.length + 1} style={tdCenter}>
                                    <ActivityIcon size={20} style={{ opacity: .4, marginBottom: 6 }} />
                                    <div style={{ fontWeight: 600, color: 'var(--ds-text-2)' }}>No usage recorded for this month</div>
                                    <div style={{ color: 'var(--ds-text-subtle)', fontSize: 'var(--ds-text-xs)', marginTop: 2 }}>
                                        Either no assistants were called, or this month is older than the 90-day retention window.
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            rows.map((r) => (
                                <tr key={r.assistant_id} className="ds-usage-row">
                                    <td style={tdStyle('left')}>
                                        <div style={{ fontWeight: 600, color: 'var(--ds-text)' }}>{namesById[r.assistant_id] || r.assistant_id}</div>
                                        <div style={{ fontFamily: 'monospace', fontSize: 'var(--ds-text-2xs)', color: 'var(--ds-text-subtle)' }}>{r.assistant_id}</div>
                                    </td>
                                    {COLUMNS.map((c) => (
                                        <td key={c.key} style={tdStyle(c.align)}>
                                            {c.key === 'failure' && Number(r.failure) > 0
                                                ? <span style={{ color: 'var(--ds-primary)', fontWeight: 600 }}>{r.failure}</span>
                                                : (Number(r[c.key]) || 0).toLocaleString()}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                    {rows.length > 0 && !loading && (
                        <tfoot>
                            <tr>
                                <td style={{ ...tdStyle('left'), fontWeight: 700, borderTop: '2px solid var(--ds-border-strong)' }}>Total</td>
                                {COLUMNS.map((c) => (
                                    <td key={c.key} style={{ ...tdStyle(c.align), fontWeight: 700, borderTop: '2px solid var(--ds-border-strong)' }}>
                                        {totals[c.key].toLocaleString()}
                                    </td>
                                ))}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
}

function thStyle(align) {
    return {
        textAlign: align, padding: '10px 14px', fontSize: 'var(--ds-text-xs)',
        letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ds-text-subtle)',
        fontWeight: 700, background: 'var(--ds-surface-2)', borderBottom: '1px solid var(--ds-border)',
        whiteSpace: 'nowrap',
    };
}
function tdStyle(align) {
    return {
        textAlign: align, padding: '11px 14px', borderTop: '1px solid var(--ds-divider)',
        color: 'var(--ds-text-2)', verticalAlign: 'middle',
    };
}
const tdCenter = { textAlign: 'center', padding: '40px 14px', color: 'var(--ds-text-muted)' };
